import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, Search, type LucideIcon } from 'lucide-react';
import {
  SEARCH_SOURCES,
  SEARCH_SOURCE_LABELS,
  SEARCH_SOURCE_PERMISSIONS,
  SEARCH_WORDS_MIN,
  classify,
  recognize,
  type SearchHit,
  type SearchSource,
} from '@asyagrup/group-hub-contracts';
import { useSession } from '@/app/session-context';
import { NAV_PREFETCH } from '@/app/nav-meta';
import { errorMessage } from '@/lib/api/client';
import { Badge, EmptyState, ErrorState, Input, Modal, cx } from '@/ui/primitives';
import { ibanLast4, sunucuGruplari, useGlobalSearch } from './api';
import { matchActions } from './actions';
import { fold, matchScreens, navKeyForHref, navLabelForHref } from './screens';
import { pruneForeignRecents, pushRecent, readRecents, type RecentEntry } from './recents';

/**
 * Genel arama paleti.
 *
 * Four things about it are decisions rather than styling:
 *
 * 1. It is PORTALLED to `document.body`. `Modal` renders inline, and the shell
 *    header is `sticky z-20`, which makes it a stacking context — a `fixed
 *    z-50` overlay mounted inside it is still confined to the header's level
 *    and would paint BEHIND the phone tab bar at `z-30`. The portal is the same
 *    escape `RailTooltip` (SideNav.tsx:489) already uses.
 * 2. It REUSES `Modal` rather than rolling an overlay. Escape, the body-scroll
 *    lock, focus restore, the phone bottom sheet with drag-to-dismiss and
 *    `role="dialog" aria-modal` all come with it — and `role="dialog"` is what
 *    every other keyboard handler in this app checks in order to stand down.
 *    The known gap, no focus trap, is inherited deliberately: one trapped
 *    dialog among twelve untrapped ones is worse than a consistent gap.
 * 3. DOM focus never leaves the input. `QueueView` moves focus between real
 *    rows because there is nothing to keep typing into; here there is, so the
 *    active row is tracked with `aria-activedescendant` while the rows stay
 *    real `<button>`s, so mouse and touch need no invention. The list owns its
 *    own scroll box, so the input cannot scroll out from under the cursor.
 * 4. The three client-side tiers — screens, actions, recents — are computed
 *    from the LIVE input and rendered at 0 ms, while only the server tier is
 *    debounced. The box is never blank for the 300 ms it takes to ask.
 */

interface PaletteRow {
  id: string;
  title: string;
  subtitle: string | null;
  /** Rendered after the subtitle, dot-separated: company name or section name. */
  meta: string | null;
  badge: string | null;
  /** Right edge, desktop only: what pressing Enter will actually do. */
  trailing: string | null;
  href: string;
  Icon: LucideIcon | null;
  /** What to remember once the row has been opened. Null = do not remember. */
  recent: Omit<RecentEntry, 'at'> | null;
}

interface PaletteGroup {
  key: string;
  label: string;
  rows: PaletteRow[];
  /** Dimmed while a newer term is in flight, so a stale list cannot pass for the answer. */
  stale?: boolean;
}

function hitRow(hit: SearchHit, key: string): PaletteRow {
  return {
    id: `gs-${key}`,
    title: hit.title,
    subtitle: hit.subtitle,
    meta: hit.companyName,
    badge: hit.badge,
    // The nine sources with no deep link land on their list screen. Saying so
    // on the row is the difference between "this opens the record" and hunting
    // for it by hand on arrival with no warning that you would have to.
    trailing: hit.exact ? 'Kaydı aç' : 'Listede aç',
    href: hit.href,
    Icon: null,
    recent: {
      source: hit.source,
      id: hit.id,
      title: hit.title,
      subtitle: hit.subtitle,
      href: hit.href,
    },
  };
}

export function SearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, can } = useSession();
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');
  const [alsoSources, setAlsoSources] = useState<SearchSource[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const userId = user?.id ?? null;

  // Opening is a fresh start: a palette that reopens on the previous search is
  // showing an answer to a question nobody just asked.
  useEffect(() => {
    if (!open) return;
    setInput('');
    setTerm('');
    setAlsoSources(null);
    setActiveIndex(0);
    pruneForeignRecents(userId);
  }, [open, userId]);

  /*
   * A pasted IBAN is cut to its last four before anything else sees it, so the
   * full number is not what gets debounced, requested, cached or echoed back.
   */
  const trimmed = input.trim();
  const last4 = ibanLast4(trimmed);
  const effective = last4 ?? trimmed;
  const refusedNationalId = recognize(effective)?.kind === 'NATIONAL_ID';

  // 300 ms is the house number (support/url-state.ts:75). The bypass is the
  // interesting half: a recognised document number commits immediately, because
  // a pasted DST-00042 is not somebody feeling their way towards a word.
  useEffect(() => {
    if (refusedNationalId) {
      setTerm('');
      return;
    }
    if (recognize(effective)?.kind === 'NUMBER') {
      setTerm(effective);
      return;
    }
    const timer = setTimeout(() => setTerm(effective), 300);
    return () => clearTimeout(timer);
  }, [effective, refusedNationalId]);

  useEffect(() => {
    setAlsoSources(null);
    setActiveIndex(0);
  }, [term]);

  // The server answers an under-length word term with empty groups; not asking
  // is the same answer without the round trip, and it is what keeps the
  // two-character floor affordable.
  const wordsTooShort = classify(term).shape === 'words' && term.length < SEARCH_WORDS_MIN;
  const enabled = open && !refusedNationalId && term.length >= 2 && !wordsTooShort;

  const primary = useGlobalSearch(term, { enabled, sources: null });
  const extra = useGlobalSearch(term, {
    enabled: enabled && alsoSources !== null,
    sources: alsoSources,
  });

  const data = primary.data;
  const serverGroups = useMemo(
    () => sunucuGruplari(data, extra.data, alsoSources !== null),
    [data, extra.data, alsoSources],
  );

  // --- rows ------------------------------------------------------------------

  const groups = useMemo<PaletteGroup[]>(() => {
    if (refusedNationalId) return [];
    const out: PaletteGroup[] = [];
    const needle = fold(input);

    // Fixed order, never score-sorted: a palette whose groups reorder on the
    // third keystroke moves the row the finger was already heading for.
    if (data?.best) {
      out.push({ key: 'best', label: 'En iyi eşleşme', rows: [hitRow(data.best, 'best')] });
    }

    const screens = matchScreens(input, can);
    if (screens.length > 0) {
      out.push({
        key: 'screens',
        label: 'Ekranlar',
        rows: screens.map((screen) => ({
          id: `gs-screen-${screen.key}`,
          title: screen.label,
          subtitle: screen.section || null,
          meta: null,
          badge: null,
          trailing: 'Ekranı aç',
          href: screen.to,
          Icon: screen.icon,
          recent: null,
        })),
      });
    }

    const actions = matchActions(input, can);
    if (actions.length > 0) {
      out.push({
        key: 'actions',
        label: 'İşlemler',
        rows: actions.map((action) => ({
          id: `gs-action-${action.key}`,
          title: action.label,
          subtitle: null,
          meta: null,
          badge: null,
          trailing: 'Aç',
          href: action.href,
          Icon: action.icon,
          recent: null,
        })),
      });
    }

    const recents = readRecents(userId).filter(
      (entry) => needle.length < 2 || fold(entry.title).includes(needle),
    );
    if (recents.length > 0) {
      out.push({
        key: 'recents',
        label: 'Son açtıklarınız',
        rows: recents.map((entry, index) => ({
          id: `gs-recent-${index}`,
          title: entry.title,
          subtitle: entry.subtitle,
          meta: null,
          badge: null,
          trailing: null,
          href: entry.href,
          Icon: null,
          recent: {
            source: entry.source,
            id: entry.id,
            title: entry.title,
            subtitle: entry.subtitle,
            href: entry.href,
          },
        })),
      });
    }

    for (const group of serverGroups) {
      const rows = group.hits.map((hit, index) => hitRow(hit, `${group.source}-${index}`));
      if (group.hasMore) {
        // "More" means: go to the source's list screen. Only /destek reads a
        // search term out of its own URL, so only that group can honestly
        // offer to continue the search there — the server decides which.
        const href = group.moreHref ?? group.hits[0]?.href ?? '/';
        const screen = navLabelForHref(href) ?? group.label;
        rows.push({
          id: `gs-more-${group.source}`,
          title: group.moreHref
            ? `Daha fazlası var — ${screen} ekranında aramayı sürdür`
            : `Daha fazlası var — ${screen} ekranını aç`,
          subtitle: null,
          meta: null,
          badge: null,
          trailing: null,
          href,
          Icon: ArrowRight,
          recent: null,
        });
      }
      out.push({
        key: `src-${group.source}`,
        label: group.label,
        rows,
        stale: primary.isFetching,
      });
    }

    return out.filter((group) => group.rows.length > 0);
  }, [data, serverGroups, input, can, userId, refusedNationalId, primary.isFetching]);

  const flat = useMemo(() => groups.flatMap((group) => group.rows), [groups]);
  const indexById = useMemo(() => new Map(flat.map((row, index) => [row.id, index])), [flat]);

  useEffect(() => {
    setActiveIndex((current) =>
      current > flat.length - 1 ? Math.max(flat.length - 1, 0) : current,
    );
  }, [flat.length]);

  const activeRow = flat[activeIndex];
  const activeId = activeRow?.id;
  const activeHref = activeRow?.href;

  // Warm the chunk the highlighted row would load, so Enter lands on a route
  // that is already downloaded. Keys absent from NAV_PREFETCH simply no-op —
  // seven nav entries have none today, and every one of them is a screen this
  // palette lands on; adding them lives in `nav-meta.ts`, its own file.
  useEffect(() => {
    if (!activeHref || !activeId) return;
    const key = navKeyForHref(activeHref);
    if (key) void NAV_PREFETCH[key]?.();
    listRef.current
      ?.querySelector<HTMLElement>(`#${CSS.escape(activeId)}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeHref, activeId]);

  const select = useCallback(
    (row: PaletteRow) => {
      if (row.recent) pushRecent(userId, row.recent);
      onClose();
      navigate(row.href);
    },
    [navigate, onClose, userId],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (flat.length === 0) return;
      // No wrapping: teleporting from the last row back to the first mid-scan
      // loses the place the eye was holding.
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActiveIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActiveIndex(flat.length - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const row = flat[activeIndex];
        if (row) select(row);
      }
      // Escape belongs to Modal; Tab stays native.
    },
    [flat, activeIndex, select],
  );

  /**
   * The footer's third line is computed HERE, from the caller's own permission
   * set and the source→permission map in contracts. Nothing about withheld data
   * crosses the wire: the server never reports which sources it declined to
   * query, because a list of those is a list of things that exist.
   */
  const { searchable, denied } = useMemo(() => {
    const yes: string[] = [];
    const no: string[] = [];
    for (const source of SEARCH_SOURCES) {
      const label = SEARCH_SOURCE_LABELS[source];
      const list = can(SEARCH_SOURCE_PERMISSIONS[source]) ? yes : no;
      if (!list.includes(label)) list.push(label);
    }
    return { searchable: yes, denied: no };
  }, [can]);

  if (!open) return null;

  const showing = flat.length > 0;
  const loading = primary.isLoading && enabled;
  const notice = noticeFor(trimmed, refusedNationalId);
  /*
   * Two answers that are FACTS ABOUT THE CALLER'S ACCOUNT rather than about the
   * data, and both have to survive having client-side rows on screen — which is
   * why they are a persistent note rather than an empty state. "Sonuç
   * bulunamadı" under either of them would be a lie: in one case the scope is
   * undefined and record search can never return anything, in the other the
   * caller holds no source permission at all. Left unsaid, both read as "the
   * search is broken" forever and the right bug is never filed.
   */
  const accountNote = data?.scopeEmpty
    ? {
        title: 'Kayıt araması için şirket kapsamınız tanımlı değil.',
        hint: 'Yalnızca ekranlar ve işlemler listeleniyor. Bu bir arama hatası değil; kapsam tanımı için yöneticinize başvurun.',
      }
    : data?.noSources
      ? {
          title: 'Kayıtlarda arama yapılamıyor.',
          hint: 'Hiçbir kayıt türü için görüntüleme yetkiniz yok. Ekranlar, işlemler ve son açtıklarınız aranmaya devam ediyor.',
        }
      : null;

  const footer = (
    <div className="text-fg-muted space-y-0.5 text-[11px] leading-snug">
      <p className="truncate">
        <span className="font-medium">Aranan alanlar:</span> Ekranlar · İşlemler
        {searchable.length > 0 ? ` · ${searchable.join(' · ')}` : ''}
      </p>
      <p>
        <span className="font-medium">Hiçbir kullanıcı için aranmayan alanlar:</span> maaş ve ücret
        bilgileri, T.C. kimlik numarası, tam IBAN, izin gerekçeleri, acil durum kişileri.
      </p>
      {denied.length > 0 && (
        <p className="truncate">
          <span className="font-medium">Yetkiniz olmadığı için aranmayan alanlar:</span>{' '}
          {denied.join(' · ')}
        </p>
      )}
      <p className="hidden pt-0.5 md:block">↑↓ gezin · ↵ aç · esc kapat</p>
    </div>
  );

  return createPortal(
    <Modal open title="Ara" wide onClose={onClose} footer={footer}>
      <div className="relative">
        <Search
          size={15}
          className="text-fg-muted pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
        />
        <Input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Kayıt, kişi, ekran veya numara ara"
          aria-label="Genel arama"
          role="combobox"
          aria-expanded={showing}
          aria-controls="gs-listbox"
          aria-activedescendant={activeId}
          autoComplete="off"
          spellCheck={false}
          className="pr-9 pl-9"
        />
        {primary.isFetching && (
          <Loader2
            size={14}
            className="text-fg-muted absolute top-1/2 right-3 -translate-y-1/2 animate-spin"
          />
        )}
      </div>

      {last4 && (
        <p className="text-fg-muted mt-2 text-xs">
          IBAN&apos;ın yalnızca son dört hanesi (…{last4}) arandı. Tam IBAN sunucuya gönderilmez.
        </p>
      )}

      {accountNote && (
        <div className="border-border bg-surface-muted/60 text-fg-muted mt-2 rounded-lg border px-3 py-2 text-xs">
          <p className="text-fg font-medium">{accountNote.title}</p>
          <p className="mt-0.5">{accountNote.hint}</p>
        </div>
      )}

      {primary.isError && (
        <div className="mt-3">
          <ErrorState
            message={errorMessage(
              primary.error,
              'Arama şu anda yanıt vermiyor. Birazdan tekrar deneyin.',
            )}
          />
        </div>
      )}

      {/* The list owns the scrolling, not the dialog body: the input has to
          stay put while the results move under it. */}
      <div
        ref={listRef}
        id="gs-listbox"
        role="listbox"
        aria-label="Arama sonuçları"
        className="overscroll-pane -mx-2 mt-2 max-h-[50dvh] min-h-0 overflow-y-auto px-2"
      >
        {groups.map((group) => (
          <div
            key={group.key}
            role="group"
            aria-label={group.label}
            className={cx('mb-1', group.stale && 'opacity-60')}
          >
            <p className="text-fg-muted px-2 pt-2 pb-1 text-[11px] font-semibold tracking-wide uppercase">
              {group.label}
            </p>
            {group.rows.map((row) => {
              const index = indexById.get(row.id) ?? -1;
              const active = index === activeIndex;
              const Icon = row.Icon;
              return (
                <button
                  key={row.id}
                  id={row.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => select(row)}
                  onMouseMove={() => setActiveIndex(index)}
                  className={cx(
                    'flex w-full touch-manipulation items-center gap-2.5 rounded-lg px-2 py-2 text-left transition',
                    active ? 'bg-surface-muted' : 'hover:bg-surface-muted/60',
                  )}
                >
                  {Icon && (
                    <span className="bg-surface-muted text-fg-muted flex size-8 shrink-0 items-center justify-center rounded-lg">
                      <Icon size={16} />
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="text-fg block truncate text-sm font-medium">{row.title}</span>
                    {(row.subtitle ?? row.meta) && (
                      <span className="text-fg-muted block truncate text-xs">
                        {[row.subtitle, row.meta].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  {row.badge && <Badge>{row.badge}</Badge>}
                  {row.trailing && (
                    <span className="text-fg-muted hidden shrink-0 text-[11px] md:inline">
                      {row.trailing}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}

        {loading && !showing && (
          // Not `<Spinner />`: its py-12 is a page-sized hole in a palette row.
          <p className="text-fg-muted flex items-center gap-2 px-2 py-6 text-sm">
            <Loader2 size={14} className="animate-spin" />
            Aranıyor…
          </p>
        )}

        {!loading && !showing && notice && <EmptyState title={notice.title} hint={notice.hint} />}

        {!loading && !showing && !notice && !accountNote && !primary.isError && (
          <EmptyState
            title={`"${data?.q ?? trimmed}" için sonuç bulunamadı.`}
            hint="Numara yapıştırdıysanız tam yazıldığından emin olun. Ekran adı, kişi adı veya cari adıyla da arayabilirsiniz."
          />
        )}

        {data && data.deferred.length > 0 && alsoSources === null && (
          <button
            type="button"
            onClick={() => setAlsoSources(data.deferred)}
            className="text-primary hover:bg-surface-muted/60 mt-1 w-full rounded-lg px-2 py-2 text-left text-xs"
          >
            Diğer kaynaklarda da ara ({data.deferred.length})
          </button>
        )}
      </div>
    </Modal>,
    document.body,
  );
}

/**
 * The one message the box owes the person when it has no rows to show.
 *
 * A refused TCKN comes first, because "sonuç bulunamadı" there is a lie about
 * the DATA when the truth is a fact about the query — and the refusal is
 * enforced on THIS side, so an eleven-digit identity number never becomes a
 * request, a query string, or a line in an access log.
 */
function noticeFor(
  trimmed: string,
  refusedNationalId: boolean,
): { title: string; hint: string } | null {
  if (refusedNationalId) {
    return {
      title: 'T.C. kimlik numarası ile arama yapılmaz.',
      hint: 'Kimlik numaraları şifreli saklanır ve hiçbir yetki düzeyinde aranamaz. Kişiyi ad, soyad, personel no veya kurum e-postasıyla arayabilirsiniz.',
    };
  }
  if (trimmed.length === 0) {
    return {
      title: 'Aramaya başlayın.',
      hint: "DST-00042, ODM-… veya RZV-2026-0001 gibi numaraları ya da IBAN'ın son dört hanesini yapıştırabilirsiniz.",
    };
  }
  if (trimmed.length < 2) {
    return {
      title: 'Aramak için en az iki karakter yazın.',
      hint: 'Numaralar iki karakterden, kelimeler üç karakterden aranır.',
    };
  }
  if (classify(trimmed).shape === 'words' && trimmed.length < SEARCH_WORDS_MIN) {
    return {
      title: 'Kelimeyle aramak için en az üç karakter yazın.',
      hint: 'Numaralar iki karakterden aranır.',
    };
  }
  return null;
}
